import { useEffect, useMemo, useState } from 'react';
import MenuRow from './../MenuRow';
import StyledLoader from '../Loader';
import { useParams } from 'react-router';
import { useSelector } from 'react-redux';
import NoOptions from '../NoOptions';
import InfiniteScroll from 'react-infinite-scroll-component';
import useFetchList from '@shared/hooks/useFetchList';
import { TABLE_KEYS, TABLE_NAMES } from 'redux_store';

const AllOptions = ({
  draft,
  mode, // eslint-disable-line no-unused-vars
  onToggleOption,
  dataKey,
  handleClose,
  open,
  tableName,
  searchValue,
  searchValueAll,
  showOnlyAvailableOptions,
  tableFilters,
  includeCurrentAccount = false,
  customAccountUuidForFilter = null,
  staticFilters,
}) => {
  const { accountUuid: paramAccountUuid } = useParams();
  const {
    metadata: { currentAccount },
  } = useSelector(state => state.user);
  const scrolableId = Date.now().toString(36) + Math.random().toString(36).substr(2);
  const scrolableIdAll = scrolableId + 'All';
  const _urlEnd = useMemo(() => {
    switch (tableName) {
      case TABLE_NAMES[TABLE_KEYS.patientsPopulationHealthTable]: {
        return TABLE_NAMES[TABLE_KEYS.alertsTable];
      }
      case TABLE_NAMES[TABLE_KEYS.allowedAccountsTable]: {
        return TABLE_NAMES[TABLE_KEYS.accountsTable];
      }
      default: {
        return tableName;
      }
    }
  }, [tableName]);

  // Static pre-filters (e.g. L2 CPT lookup needs
  // `q=targetAccountUuid:<uuid>` to scope the CPT catalogue to the
  // drilled-into account). Merged with the other columns' filters
  // before we call the lookup, and included in both search-driven
  // and full-list variants below.
  const staticFilterEntries = Array.isArray(staticFilters) ? staticFilters : [];
  const othersFieldsFilters = [
    ...tableFilters.filter(({ id }) => id !== dataKey),
    ...staticFilterEntries,
  ];

  const {
    items,
    totalCount,
    loading: dataLoading,
    onLoadMoreRows,
    onMultiColumnFilter,
  } = useFetchList({
    baseEndpoint: `/accounts/${
      customAccountUuidForFilter || paramAccountUuid || currentAccount.uuid
    }/lookups/${_urlEnd}`,
    baseParams: { fields: [dataKey], includeCurrentAccount: includeCurrentAccount },
    canLoad: true,
  });

  const {
    items: itemsAll,
    totalCount: totalCountAll,
    loading: dataLoadingAll,
    onLoadMoreRows: onLoadMoreRowsAll,
    onMultiColumnFilter: onMultiColumnFilterAll,
  } = useFetchList({
    ignorePrefilters: true,
    baseEndpoint: `/accounts/${
      customAccountUuidForFilter || paramAccountUuid || currentAccount.uuid
    }/lookups/${_urlEnd}`,
    baseParams: { fields: [dataKey], includeCurrentAccount: includeCurrentAccount },
    canLoad: true,
  });

  useEffect(() => {
    onMultiColumnFilter([...othersFieldsFilters, { id: dataKey, value: searchValue }], true);
  }, [searchValue]);

  useEffect(() => {
    onMultiColumnFilterAll([{ id: dataKey, value: searchValueAll }], true);
  }, [searchValueAll]);

  useEffect(() => {
    if (open) {
      if (showOnlyAvailableOptions) {
        onMultiColumnFilter([...othersFieldsFilters, { id: dataKey, value: '' }], false);
      } else {
        onMultiColumnFilterAll([{ id: dataKey, value: '' }], false);
      }
    }
  }, [open]);

  const { include = [], exclude = [] } = draft || {};

  // Where an option currently sits, for the row's own rendering.
  const stateOf = option => {
    if (include.includes(option)) return 'include';
    if (exclude.includes(option)) return 'exclude';
    return 'none';
  };

  // Excluded values are pinned to the top of the pre-filter list.
  //
  // That list shows the values present in the current result set — and
  // an excluded value has no rows there by definition, so it would drop
  // out of the list the moment it was excluded, taking with it the only
  // way to un-exclude it. Pinning keeps the action reversible.
  const withPinnedExclusions = list => {
    const present = list || [];
    const missing = exclude.filter(option => !present.includes(option));
    return missing.length ? [...missing, ...present] : present;
  };

  // Both lists are kept mounted so InfiniteScroll's internal state
  // doesn't reset when toggling between Pre-Filter and All; we just
  // hide the inactive one via `display: none`.
  return (
    <>
      <div
        style={{
          flex: 1,
          minHeight: 0,
          display: showOnlyAvailableOptions ? 'flex' : 'none',
          flexDirection: 'column',
        }}
      >
        <_Content
          items={withPinnedExclusions(items)}
          totalCount={totalCount}
          dataLoading={dataLoading}
          onLoadMoreRows={onLoadMoreRows}
          scrolableId={scrolableId}
          stateOf={stateOf}
          onOptionCheckboxClick={onToggleOption}
          handleClose={handleClose}
          othersFieldsFilters={[]}
        />
      </div>
      <div
        style={{
          flex: 1,
          minHeight: 0,
          display: !showOnlyAvailableOptions ? 'flex' : 'none',
          flexDirection: 'column',
        }}
      >
        <_Content
          items={itemsAll}
          totalCount={totalCountAll}
          dataLoading={dataLoadingAll}
          onLoadMoreRows={onLoadMoreRowsAll}
          scrolableId={scrolableIdAll}
          stateOf={stateOf}
          onOptionCheckboxClick={onToggleOption}
          handleClose={handleClose}
          othersFieldsFilters={othersFieldsFilters}
        />
      </div>
    </>
  );
};

const _Content = ({
  items,
  totalCount,
  dataLoading,
  onLoadMoreRows,
  stateOf,
  handleClose,
  scrolableId,
  onOptionCheckboxClick,
}) => {
  const { titles } = useSelector(state => state.i18n);
  const hasOptionsFromApi = !!(items && items?.length);

  const [isLoadMore, setIsLoadMore] = useState(false);

  useEffect(() => {
    if (!dataLoading) {
      setIsLoadMore(false);
    }
  }, [dataLoading]);

  return (
    <div id={scrolableId} className="qhs-dynh-list" role="listbox">
      <InfiniteScroll
        dataLength={totalCount || 0}
        next={() => {}}
        initialScrollY={10}
        scrollableTarget={scrolableId}
        onScroll={e => {
          if (e.target.scrollTop + 500 >= e.target.scrollHeight) {
            if (!dataLoading && onLoadMoreRows && (items || []).length < totalCount) {
              setIsLoadMore(true);
              onLoadMoreRows((items || []).length);
            }
          }
        }}
        hasMore={true}
        loader={
          isLoadMore ? (
            <div style={{ padding: '8px 4px' }}>
              <StyledLoader />
            </div>
          ) : (
            <></>
          )
        }
      >
        {(!dataLoading || isLoadMore) &&
          items &&
          items.map(option => (
            <MenuRow
              key={option}
              option={option}
              state={stateOf(option)}
              onItemClick={onOptionCheckboxClick}
              applyCurrentFilter={e => handleClose(e, option)}
            />
          ))}
      </InfiniteScroll>
      {dataLoading && !isLoadMore && <StyledLoader />}
      {!dataLoading && !hasOptionsFromApi && (
        <NoOptions title={titles?.nothingFound || 'No options found'} />
      )}
    </div>
  );
};

export default AllOptions;
